import { network } from '@round/utils';
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAllPostPaymentDetails } from '@round/creatorbase-helpers/api-hooks';

type Params = {
    postId: string;
    postPaymentRequestId?: number | null | undefined;
};

export function useCurrentPaymentDetails(params: Params, config?: { enabled: boolean }) {
    const queryClient = useQueryClient();

    const {
        data: postPaymentDetails,
        isFetching: arePostPaymentDetailsFetching,
        isLoading: isPostPaymentDetailsLoading,
        isSuccess: postPaymentDetailsIsSuccess,
        error: postPaymentDetailsError,
        invalidate: invalidatePostPaymentDetails,
    } = useAllPostPaymentDetails(
        { post_id: params.postId },
        {
            ...config,
            select: (data) =>
                data.find((detail) => detail.payment_request_status === 'UNREQUESTED') ||
                data.find((detail) => detail.payment_request_id === params.postPaymentRequestId) ||
                null,
        }
    );

    const fileKey = ['current-payment-details-file', postPaymentDetails?.file_upload];
    const fileQuery = useQuery({
        queryKey: fileKey,
        queryFn: !postPaymentDetails?.file_upload
            ? skipToken
            : async () => {
                  const response = await network.fetchFile(postPaymentDetails?.file_upload!);
                  if (response instanceof File) {
                      return response;
                  }

                  throw new Error('Could not fetch file');
              },
        enabled: !!postPaymentDetails?.file_upload,
    });

    const invalidateFile = () => {
        queryClient.resetQueries({ queryKey: fileKey });
    };

    return {
        postPaymentDetails: postPaymentDetails,
        file: fileQuery.data || null,
        hasFileError: fileQuery.error,
        hasError: !!postPaymentDetailsError,
        isFetching: arePostPaymentDetailsFetching || fileQuery.isFetching,
        isLoading: isPostPaymentDetailsLoading,
        isSuccess: postPaymentDetailsIsSuccess,
        invalidate: () => {
            invalidatePostPaymentDetails();
            invalidateFile();
        },
    };
}
